import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faVial,
  faCalendar,
  faCapsules,
  faPrint,
  faFileUpload,
  faEdit,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import { useDropzone } from "react-dropzone";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// ฟังก์ชันแปลงสถานะเป็นข้อความไทย
const getStatusText = (status) => {
  switch (status) {
    case "Draft":
      return "ร่างใบเบิก";
    case "Pending":
      return "รออนุมัติ";
    case "Approved":
      return "รอรับเข้าคลัง";
    case "Completed":
      return "รับเข้าคลังแล้ว";
    case "Cancelled":
      return "ยกเลิกใบเบิก";
    default:
      return status || "";
  }
};

// Printable Requisition Form Component
const PrintableRequisitionForm = ({ formData, parentWarehouses, warehouses, vaccineOptions }) => {
  console.log("PrintableRequisitionForm rendered with formData:", formData);
  console.log("Parent Warehouses in Printable:", parentWarehouses);
  console.log("Warehouses in Printable:", warehouses);

  const getWarehouseName = (warehouseId, isParent) => {
    if (isParent) {
      const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    }
    const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
    return warehouse ? warehouse.WarehouseName : "-";
  };

  return (
    <Box
      style={{
        display: "none",
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "100%",
        padding: "40px",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
      }}
      className="printable"
    >
      <style>
        {`
          @media print {
            .printable {
              display: block !important;
              position: static !important;
              width: 100% !important;
              height: auto !important;
            }
            body * {
              visibility: hidden;
            }
            .printable, .printable * {
              visibility: visible;
            }
          }
        `}
      </style>
      <Box textAlign="center" mb={8}>
        <Heading as="h1" size="xl">
          ใบเบิกวัคซีน
        </Heading>
      </Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={8}>
        <Box>
          <Text fontWeight="bold">เลขที่เอกสาร:</Text>
          <Text>{formData.TransactionID || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">วันที่ขอเบิก:</Text>
          <Text>
            {formData.RequestDate
              ? formData.RequestDate.toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "-"}
          </Text>
        </Box>
        <Box>
          <Text fontWeight="bold">คลังต้นทาง:</Text>
          <Text>{getWarehouseName(formData.FromWarehouseID, true) || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">คลังปลายทาง:</Text>
          <Text>{getWarehouseName(formData.ToWarehouseID, false) || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทงบประมาณ:</Text>
          <Text>
            {formData.BudgetType || "-"}
          </Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทการเบิก:</Text>
          <Text>{formData.RequestType || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทเอกสารอ้างอิง:</Text>
          <Text>{formData.ReferenceDocumentType || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">รอบการเบิก:</Text>
          <Text>{formData.ReferenceDocumentNo || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">เจ้าหน้าที่คลัง:</Text>
          <Text>{formData.WarehouseStaffID || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">สถานะ:</Text>
          <Text>{getStatusText(formData.Status) || "-"}</Text>
        </Box>
        <Box gridColumn="1 / -1">
          <Text fontWeight="bold">รายละเอียดเพิ่มเติม:</Text>
          <Text>{formData.RequestDetail || "-"}</Text>
        </Box>
      </Grid>
      <Heading as="h2" size="md" mb={4}>
        รายการวัคซีนรับเข้าคลัง
      </Heading>
      <Table variant="simple" borderWidth={1} mb={8}>
        <Thead>
          <Tr>
            <Th borderWidth={1}>รหัสวัคซีน</Th>
            <Th borderWidth={1}>ชื่อวัคซีน</Th>
            <Th borderWidth={1}>Lot No.</Th>
            <Th borderWidth={1}>จำนวน (ขวด)</Th>
            <Th borderWidth={1}>จำนวนโดส</Th>
            <Th borderWidth={1}>วันที่ขอเบิก</Th>
          </Tr>
        </Thead>
        <Tbody>
          {formData.VaccineDetails.length > 0 ? (
            formData.VaccineDetails.map((detail, index) => (
              <Tr key={index}>
                <Td borderWidth={1}>{detail.VaccineID || "-"}</Td>
                <Td borderWidth={1}>{detail.name || "-"}</Td>
                <Td borderWidth={1}>{detail.LotID || "-"}</Td>
                <Td borderWidth={1}>{detail.RequestQuantity.toLocaleString()}</Td>
                <Td borderWidth={1}>{detail.DoseCount.toLocaleString()}</Td>
                <Td borderWidth={1}>
                  {detail.RequestDate
                    ? detail.RequestDate.toLocaleDateString("th-TH", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "-"}
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                ไม่มีรายการวัคซีน
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <Box textAlign="right" mt={8}>
        <Text>___________________________</Text>
        <Text>ผู้จัดทำ</Text>
      </Box>
    </Box>
  );
};

const VaccinePreventionRequisitionForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "RQ-XXXX-XX-XX",
    RequestDate: new Date(),
    FromWarehouseID: "",
    ToWarehouseID: "",
    BudgetType: "",
    BudgetYear: "",
    RequestUnit: "",
    RequestType: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: profile?.Email || "",
    RequestDetail: "",
    Status: "Draft",
    VaccineDetails: [],
    deliveryAddress: "",
    deliveryBy: "",
    deliveryVehicle: "",
    deliveryStaff: "",
    deliveryRoute: "",
    trackingNumber: "",
  });
  const [parentWarehouses, setParentWarehouses] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    LotID: "",
    RequestQuantity: "",
    RequestDate: formData.RequestDate,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [temperatureFile, setTemperatureFile] = useState(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "application/pdf": [],
      "application/xlsx": [],
      "application/xls": [],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setTemperatureFile(file);
        setIsEditingFile(false);
      }
    },
  });

  const handleEditFile = () => {
    setIsEditingFile(true);
  };

  const handleRemoveFile = () => {
    setTemperatureFile(null);
    setIsEditingFile(false);
  };

  const printTable = () => {
    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText = formData.BudgetType || "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Requisition - ${formData.TransactionID}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1, h2 { text-align: center; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
            .grid-item { margin-bottom: 8px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 32px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h1>ใบเบิกวัคซีน</h1>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${formData.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${
                formData.RequestDate
                  ? formData.RequestDate.toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "-"
              }
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(formData.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(formData.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${formData.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${formData.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${formData.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${formData.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(formData.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${formData.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนรับเข้าคลัง</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวนโดส</th>
                <th>วันที่ขอเบิก</th>
              </tr>
            </thead>
            <tbody>
              ${
                formData.VaccineDetails.length > 0
                  ? formData.VaccineDetails.map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td>${detail.RequestQuantity.toLocaleString()}</td>
                          <td>${detail.DoseCount.toLocaleString()}</td>
                          <td>${
                            detail.RequestDate
                              ? detail.RequestDate.toLocaleDateString("th-TH", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "-"
                          }</td>
                        </tr>
                      `
                    ).join("")
                  : `
                        <tr>
                          <td colspan="6" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            ___________________________<br>
            ผู้จัดทำ
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  const printDeliveryNote = () => {
    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText = formData.BudgetType || "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Delivery Note - ${formData.TransactionID}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1, h2 { text-align: center; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
            .grid-item { margin-bottom: 8px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 32px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h1>ใบนำส่งวัคซีน</h1>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${formData.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${
                formData.RequestDate
                  ? formData.RequestDate.toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "-"
              }
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(formData.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(formData.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${formData.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${formData.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${formData.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${formData.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(formData.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${formData.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่นำส่ง</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวนโดส</th>
                <th>วันที่ขอเบิก</th>
              </tr>
            </thead>
            <tbody>
              ${
                formData.VaccineDetails.length > 0
                  ? formData.VaccineDetails.map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td>${detail.RequestQuantity.toLocaleString()}</td>
                          <td>${detail.DoseCount.toLocaleString()}</td>
                          <td>${
                            detail.RequestDate
                              ? detail.RequestDate.toLocaleDateString("th-TH", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "-"
                          }</td>
                        </tr>
                      `
                    ).join("")
                  : `
                        <tr>
                          <td colspan="6" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            ___________________________<br>
            ผู้จัดทำ
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  const printInspectionNote = () => {
    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText = formData.BudgetType || "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Inspection Note - ${formData.TransactionID}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1, h2 { text-align: center; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
            .grid-item { margin-bottom: 8px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 32px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h1>ใบตรวจนับวัคซีน</h1>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${formData.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${
                formData.RequestDate
                  ? formData.RequestDate.toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "-"
              }
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(formData.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(formData.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${formData.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${formData.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${formData.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${formData.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(formData.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${formData.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่ตรวจนับ</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวนโดส</th>
                <th>วันที่ขอเบิก</th>
                <th>การตรวจสอบ</th>
              </tr>
            </thead>
            <tbody>
              ${
                formData.VaccineDetails.length > 0
                  ? formData.VaccineDetails.map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td>${detail.RequestQuantity.toLocaleString()}</td>
                          <td>${detail.DoseCount.toLocaleString()}</td>
                          <td>${
                            detail.RequestDate
                              ? detail.RequestDate.toLocaleDateString("th-TH", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "-"
                          }</td>
                          <td>__________</td>
                        </tr>
                      `
                    ).join("")
                  : `
                        <tr>
                          <td colspan="7" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            ___________________________<br>
            ผู้จัดทำ
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  const printPackingLabel = async (row) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: {
          WarehouseID: formData.FromWarehouseID,
          BudgetType: formData.BudgetType,
          VaccineID: row.VaccineID,
          LotID: row.LotID,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.InventoryReport?.length > 0) {
        const inventoryData = response.InventoryReport[0];
        const productionDate = new Date(inventoryData.ProductionDate).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        const expiryDate = new Date(inventoryData.ExpiryDate).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        const printContent = `
          <html>
            <head>
              <title>Packing Label - ${row.VaccineID} - ${row.LotID}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 20px; 
                  text-align: center; 
                }
                h1 { 
                  font-size: 20px; 
                  margin-bottom: 10px; 
                }
                .label { 
                  border: 1px solid black; 
                  padding: 15px; 
                  display: inline-block; 
                  text-align: left; 
                  width: 300px; 
                }
                .label div { 
                  margin-bottom: 8px; 
                }
                .label div span { 
                  font-weight: bold; 
                }
              </style>
            </head>
            <body>
              <h1>Packing Label</h1>
              <div class="label">
                <div><span>ชื่อวัคซีน:</span> ${row.name || "-"}</div>
                <div><span>Lot:</span> ${row.LotID || "-"}</div>
                <div><span>จำนวน:</span> ${row.RequestQuantity.toLocaleString()} ขวด</div>
                <div><span>วันที่ผลิต:</span> ${productionDate}</div>
                <div><span>วันหมดอายุ:</span> ${expiryDate}</div>
              </div>
            </body>
          </html>
        `;
        const newWindow = window.open("", "", "width=400,height=300");
        newWindow.document.write(printContent);
        newWindow.document.close();
        newWindow.print();
      } else {
        alert("ไม่พบข้อมูลสำหรับ Lot นี้ในคลัง");
      }
    } catch (error) {
      console.error("Error fetching inventory for packing label:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูลสำหรับ Packing Label: " + error.message);
    }
  };

  // Sync newDetail.RequestDate with formData.RequestDate
  useEffect(() => {
    setNewDetail((prev) => ({ ...prev, RequestDate: formData.RequestDate }));
  }, [formData.RequestDate]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchParentWarehouses = async () => {
    try {
      console.log("Fetching parent warehouses with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineParentWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Parent Warehouses API Response:", response);
      if (response.result === "success") {
        const warehouses = response.VaccineWarehousesList || response.VaccineParentWarehousesList || response.ParentWarehousesList || [];
        console.log("Setting parentWarehouses:", warehouses);
        setParentWarehouses(warehouses);
        return warehouses;
      } else {
        console.warn("Parent Warehouses API returned non-success result:", response);
        setParentWarehouses([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching parent warehouses:", error);
      setParentWarehouses([]);
      return [];
    }
  };

  const fetchWarehouses = async () => {
    try {
      console.log("Fetching warehouses with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Warehouses API Response:", response);
      if (response.result === "success") {
        const warehouses = response.VaccineWarehousesList || [];
        console.log("Setting warehouses:", warehouses);
        setWarehouses(warehouses);
        return warehouses;
      } else {
        console.warn("Warehouses API returned non-success result:", response);
        setWarehouses([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      setWarehouses([]);
      return [];
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      console.log("Fetching vaccine options with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Vaccine Options API Response:", response);
      if (response.result === "success") {
        const vaccines = response.VaccinesList || [];
        setVaccineOptions(vaccines);
        return vaccines;
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchInventory = async (warehouseId, budgetType, vaccineId) => {
    if (!warehouseId || !budgetType || !vaccineId) {
      setInventory([]);
      return;
    }
    try {
      console.log("Fetching inventory for WarehouseID:", warehouseId, "BudgetType:", budgetType, "VaccineID:", vaccineId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: {
          WarehouseID: warehouseId,
          BudgetType: budgetType,
          VaccineID: vaccineId,
        },
        sessionId: sessionId || "",
      });
      console.log("Inventory API Response:", response);
      if (response.result === "success") {
        const sortedInventory = (response.InventoryReport || []).sort(
          (a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate)
        );
        setInventory(sortedInventory);
      } else {
        setInventory([]);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      setInventory([]);
    }
  };

  const fetchRequisition = async (id, vaccines) => {
    setIsLoading(true);
    try {
      console.log("Fetching requisition for TransactionID:", id);
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePreventionRequisitionsByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      console.log("Requisition API Response:", response);
      if (response.result === "success") {
        const requisition = response.VaccineRequisition;
        const details = response.VaccineDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            RequestDate: new Date(detail.RequestDate),
            DoseCount: doseCount,
          };
        });
        setFormData({
          TransactionID: requisition.TransactionID,
          RequestDate: new Date(requisition.RequestDate),
          FromWarehouseID: requisition.FromWarehouseID,
          ToWarehouseID: requisition.ToWarehouseID,
          BudgetType: requisition.BudgetType,
          BudgetYear: requisition.BudgetYear,
          RequestUnit: requisition.RequestUnit,
          RequestType: requisition.RequestType,
          ReferenceDocumentType: requisition.ReferenceDocumentType,
          ReferenceDocumentNo: requisition.ReferenceDocumentNo,
          WarehouseStaffID: requisition.WarehouseStaffID,
          RequestDetail: requisition.RequestDetail,
          Status: requisition.Status,
          VaccineDetails: updatedDetails,
          deliveryAddress: requisition.deliveryAddress || "",
          deliveryBy: requisition.deliveryBy || "",
          deliveryVehicle: requisition.deliveryVehicle || "",
          deliveryStaff: requisition.deliveryStaff || "",
          deliveryRoute: requisition.deliveryRoute || "",
          trackingNumber: requisition.trackingNumber || "",
        });
        if (details.length > 0) {
          fetchInventory(
            requisition.FromWarehouseID,
            requisition.BudgetType,
            details[0].VaccineID
          );
        }
      }
    } catch (error) {
      console.error("Error fetching requisition:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        console.log("Starting loadData with sessionId:", sessionId);
        const [parentWarehousesResult, warehousesResult] = await Promise.all([
          fetchParentWarehouses(),
          fetchWarehouses(),
        ]);
        console.log("loadData - Parent Warehouses:", parentWarehousesResult);
        console.log("loadData - Warehouses:", warehousesResult);
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchRequisition(transactionId, vaccines);
        }
      } else {
        console.warn("No sessionId, skipping loadData");
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  useEffect(() => {
    console.log("parentWarehouses state updated:", parentWarehouses);
  }, [parentWarehouses]);

  useEffect(() => {
    console.log("warehouses state updated:", warehouses);
  }, [warehouses]);

  useEffect(() => {
    if (
      formData.FromWarehouseID &&
      formData.BudgetType &&
      newDetail.VaccineID
    ) {
      fetchInventory(
        formData.FromWarehouseID,
        formData.BudgetType,
        newDetail.VaccineID
      );
    } else {
      setInventory([]);
    }
  }, [formData.FromWarehouseID, formData.BudgetType, newDetail.VaccineID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`);
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (date) => {
    console.log("Date changed:", date);
    setFormData({ ...formData, RequestDate: date });
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Detail input changed: ${name} = ${value}`);
    setNewDetail({ ...newDetail, [name]: value });
  };

  const handleDetailDateChange = (date) => {
    console.log("Detail date changed:", date);
    setNewDetail({ ...newDetail, RequestDate: date });
  };

  const handleQuantityChange = (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    console.log(`Quantity changed for VaccineID: ${vaccineId}, LotID: ${lotId}, New Quantity: ${newQuantity}`);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 1;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, DoseCount: newDoseCount };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleLotChange = (vaccineId, oldLotId, newLotId) => {
    console.log(`Lot changed for VaccineID: ${vaccineId}, Old LotID: ${oldLotId}, New LotID: ${newLotId}`);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === oldLotId) {
        return { ...detail, LotID: newLotId };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "FromWarehouseID":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
  };

  const isFormValid = () => {
    const valid = formData.FromWarehouseID && Object.keys(errors).length === 0;
    console.log("Form valid:", valid);
    return valid;
  };

  const handleAddDetailWithCheckStock = () => {
    console.log("Adding detail:", newDetail);
    if (!newDetail.VaccineID || newDetail.RequestQuantity <= 0) {
      alert("กรุณาเลือกวัคซีนและระบุจำนวนที่มากกว่า 0");
      return;
    }

    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newDetail.VaccineID
    );
    if (!selectedVaccine) {
      console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      return;
    }

    const packageSize = selectedVaccine.PackageSize || 1;
    const requestQuantity = parseInt(newDetail.RequestQuantity);
    const doseCount = Math.ceil(requestQuantity * packageSize);

    if (newDetail.LotID) {
      const selectedLot = inventory.find(
        (item) => item.LotID === newDetail.LotID
      );
      if (!selectedLot) {
        alert("Lot ที่เลือกไม่พบในคลัง");
        return;
      }
      if (requestQuantity > selectedLot.TotalQuantity) {
        alert(
          `จำนวนที่ขอเบิก (${requestQuantity}) เกินจำนวนคงคลังของ Lot ${selectedLot.LotID} (${selectedLot.TotalQuantity})`
        );
        return;
      }
      const detailData = {
        VaccineID: newDetail.VaccineID,
        name: selectedVaccine.VaccineNameTH,
        LotID: newDetail.LotID,
        RequestQuantity: requestQuantity,
        RequestDate: newDetail.RequestDate || formData.RequestDate,
        DoseCount: doseCount,
      };
      setFormData((prevFormData) => ({
        ...prevFormData,
        VaccineDetails: [...prevFormData.VaccineDetails, detailData],
      }));
    } else {
      let remainingQuantity = requestQuantity;
      const detailsToAdd = [];
      for (const lot of inventory) {
        if (remainingQuantity <= 0) break;
        const quantityToTake = Math.min(
          remainingQuantity,
          lot.TotalQuantity
        );
        if (quantityToTake > 0) {
          const lotDoseCount = Math.ceil(quantityToTake * packageSize);
          detailsToAdd.push({
            VaccineID: newDetail.VaccineID,
            name: selectedVaccine.VaccineNameTH,
            LotID: lot.LotID,
            RequestQuantity: quantityToTake,
            RequestDate: newDetail.RequestDate || formData.RequestDate,
            DoseCount: lotDoseCount,
          });
          remainingQuantity -= quantityToTake;
        }
      }
      if (remainingQuantity > 0) {
        alert(
          `จำนวนที่ขอเบิก (${requestQuantity}) เกินจำนวนคงคลังทั้งหมด (${requestQuantity - remainingQuantity})`
        );
        return;
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        VaccineDetails: [...prevFormData.VaccineDetails, ...detailsToAdd],
      }));
    }

    setNewDetail({
      VaccineID: "",
      LotID: "",
      RequestQuantity: "",
      RequestDate: formData.RequestDate,
    });
    setInventory([]);
  };

  
  const handleAddDetail = () => {
    console.log("Adding detail:", newDetail);
    if (!newDetail.VaccineID || newDetail.RequestQuantity <= 0) {
      alert("กรุณาเลือกวัคซีนและระบุจำนวนที่มากกว่า 0");
      return;
    }

    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newDetail.VaccineID
    );
    if (!selectedVaccine) {
      console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      return;
    }

    const packageSize = selectedVaccine.PackageSize || 1;
    const requestQuantity = parseInt(newDetail.RequestQuantity);
    const doseCount = Math.ceil(requestQuantity * packageSize);

    
    const detailData = {
      VaccineID: newDetail.VaccineID,
      name: selectedVaccine.VaccineNameTH,
      LotID: "", //newDetail.LotID,
      RequestQuantity: requestQuantity,
      RequestDate: newDetail.RequestDate || formData.RequestDate,
      DoseCount: doseCount,
    };
    setFormData((prevFormData) => ({
      ...prevFormData,
      VaccineDetails: [...prevFormData.VaccineDetails, detailData],
    }));

    setNewDetail({
      VaccineID: "",
      LotID: "",
      RequestQuantity: "",
      RequestDate: formData.RequestDate,
    });
    setInventory([]);
  };

  const handleRemoveDetail = (row) => {
    console.log("Removing detail:", row);
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
      ),
    });
  };

  const handleSubmit = async () => {
    console.log("Submitting form:", formData);
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const formattedRequestDate = formData.RequestDate
        ? `${formData.RequestDate.getFullYear()}-${(formData.RequestDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${formData.RequestDate
            .getDate()
            .toString()
            .padStart(2, "0")}`
        : null;
      const response = await apiService({
        method: "POST",
        url: "/createVaccinePreventionRequisitions",
        data: { ...formData, RequestDate: formattedRequestDate, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      console.log("Submit API Response:", response);
      if (response.result === "success") {
        setFormData({ ...formData, TransactionID: response.TransactionID });
        setIsSubmitted(true);
        alert("สร้างเอกสารสำเร็จ! เลขที่เอกสาร: " + response.TransactionID);
      }
    } catch (error) {
      console.error("Error creating requisition:", error);
      alert("เกิดข้อผิดพลาดในการสร้างเอกสาร: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (overrideData = {}) => {
    const mergedFormData = {
      ...formData,
      ...overrideData,
    };
    console.log("Confirming form:", formData);
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    const formattedRequestDate = formData.RequestDate
      ? `${formData.RequestDate.getFullYear()}-${(formData.RequestDate.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${formData.RequestDate
          .getDate()
          .toString()
          .padStart(2, "0")}`
      : null;
    const submitData = {
      ...mergedFormData,
      RequestDate: formattedRequestDate,
      VaccineDetails: formData.VaccineDetails.map((detail) => ({
        ...detail,
        RequestDate: detail.RequestDate
          ? `${detail.RequestDate.getFullYear()}-${(detail.RequestDate.getMonth() + 1)
              .toString()
              .padStart(2, "0")}-${detail.RequestDate
              .getDate()
              .toString()
              .padStart(2, "0")}`
          : null,
      })),
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/receivedVaccinePreventionRequisitionsByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      console.log("Confirm API Response:", response);
      if (response.result === "success") {
        alert("บันทึกใบเบิกสำเร็จ!");
        navigate("/vaccinepreventionreceivelist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };  

  const isEditable = !isSubmitted;

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
      width: "350px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนโดส</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.DoseCount.toLocaleString()}
        </Box>
      ),
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalendar} />
          <span>วันที่ขอเบิก</span>
        </HStack>
      ),
      selector: (row) =>
        row.RequestDate
          ? row.RequestDate.toLocaleDateString("th-TH", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) => (
        <HStack spacing={2}>
          {/*
          <IconButton.DeleteIcon
            onClick={() => handleRemoveDetail(row)}
            size="sm"
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={faTag} />}
            size="sm"
            colorScheme="green"
            onClick={() => printPackingLabel(row)}
          >
            พิมพ์ Packing Label
          </Button>
           */}
        </HStack>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "250px",
    },
  ];

  if (!sessionId) {
    console.log("No sessionId, rendering null");
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <PrintableRequisitionForm
        formData={formData}
        parentWarehouses={parentWarehouses}
        warehouses={warehouses}
        vaccineOptions={vaccineOptions}
      />
      <Heading as="h2" mb={10} textAlign="center">
        รายการวัคซีนรับเข้าคลัง
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลเอกสารการเบิกวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่ขอเบิก
              </FormLabel>
              <CustomDatePicker
                value={formData.RequestDate}
                onChange={handleDateChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.FromWarehouseID}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                คลังต้นทาง
              </FormLabel>
              <Select
                name="FromWarehouseID"
                value={formData.FromWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังต้นทาง"
                colorScheme="iBlue"
              >
                {console.log("Rendering FromWarehouseID dropdown with parentWarehouses:", parentWarehouses)}
                {parentWarehouses.length > 0 ? (
                  parentWarehouses.map((warehouse) => (
                    <option
                      key={warehouse.WarehouseID}
                      value={warehouse.WarehouseID}
                    >
                      {warehouse.WarehouseName}
                    </option>
                  ))
                ) : (
                  <option disabled>ไม่มีข้อมูลคลังต้นทาง</option>
                )}
              </Select>
              <FormErrorMessage>{errors.FromWarehouseID}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                คลังปลายทาง
              </FormLabel>
              <Select
                name="ToWarehouseID"
                value={formData.ToWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังปลายทาง (ถ้ามี)"
                colorScheme="iBlue"
                isDisabled={isSubmitted}
              >
                {console.log("Rendering ToWarehouseID dropdown with warehouses:", warehouses)}
                {warehouses.length > 0 ? (
                  warehouses.map((warehouse) => (
                    <option
                      key={warehouse.WarehouseID}
                      value={warehouse.WarehouseID}
                    >
                      {warehouse.WarehouseName}
                    </option>
                  ))
                ) : (
                  <option disabled>ไม่มีข้อมูลคลังปลายทาง</option>
                )}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired>
              <FormLabel fontSize="xl" color="gray">
                ประเภทงบประมาณ
              </FormLabel>
              <Input
                name="BudgetType"
                value={formData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทงบประมาณ"
                colorScheme="iBlue"
                readOnly
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired>
              <FormLabel fontSize="xl" color="gray">
                ปีงบประมาณ
              </FormLabel>
              <Input
                name="BudgetYear"
                value={formData.BudgetYear}
                onChange={handleInputChange}
                placeholder="ปีงบประมาณ"
                colorScheme="iBlue"
                readOnly
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired>
              <FormLabel fontSize="xl" color="gray">
                กลุ่มงาน
              </FormLabel>
              <Input
                name="RequestUnit"
                value={formData.RequestUnit}
                onChange={handleInputChange}
                placeholder="ปีงบประมาณ"
                colorScheme="iBlue"
                readOnly
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired>
              <FormLabel fontSize="xl" color="gray">
                รอบการเบิก
              </FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={formData.ReferenceDocumentNo}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>          
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทการเบิก
              </FormLabel>
              <Input
                name="RequestType"
                value={formData.RequestType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทเอกสารอ้างอิง
              </FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={formData.ReferenceDocumentType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เจ้าหน้าที่คลัง
              </FormLabel>
              <Input
                name="WarehouseStaffID"
                value={formData.WarehouseStaffID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                รายละเอียดเพิ่มเติม
              </FormLabel>
              <Textarea
                name="RequestDetail"
                value={formData.RequestDetail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                สถานะ
              </FormLabel>
              <Input
                name="Status"
                value={getStatusText(formData.Status)}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel
                onClick={() => navigate("/vaccinepreventionreceivelist")}
              />
              <IconButton.Submit label="สร้างเอกสาร" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>
      {isSubmitted && (
        <>
{/*
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iPurple.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                <Heading size="lg" fontWeight="normal">
                  เพิ่มรายการวัคซีนรับเข้าคลัง
                </Heading>
              </HStack>
            </Box>
            <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วัคซีน
                </FormLabel>
                <Select
                  name="VaccineID"
                  value={newDetail.VaccineID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iPurple"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  จำนวน (ขวด)
                </FormLabel>
                <Input
                  name="RequestQuantity"
                  type="number"
                  value={newDetail.RequestQuantity}
                  onChange={handleDetailInputChange}
                  min={1}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  Lot No.
                </FormLabel>
                <Select
                  name="LotID"
                  value={newDetail.LotID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือก Lot หรือปล่อยว่างเพื่อใช้ FIFO"
                  colorScheme="iPurple"
                >
                  {inventory.map((item) => (
                    <option key={item.LotID} value={item.LotID}>
                      {item.LotID}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วันที่ขอเบิก
                </FormLabel>
                <CustomDatePicker
                  value={newDetail.RequestDate}
                  onChange={handleDetailDateChange}
                  colorScheme="iPurple"
                />
              </FormControl>
            </Grid>
            {inventory.length > 0 && (
              <Box mt={4} p={4} borderWidth={1} borderRadius="md">
                <Heading size="md" mb={4}>
                  ข้อมูลวัคซีนคงคลัง
                </Heading>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Lot No.</Th>
                      <Th>จำนวนคงคลัง (ขวด)</Th>
                      <Th>วันที่ผลิต</Th>
                      <Th>วันหมดอายุ</Th>
                      <Th>อายุคงเหลือ (วัน)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {inventory.map((item) => (
                      <Tr key={item.LotID}>
                        <Td>{item.LotID}</Td>
                        <Td>{item.TotalQuantity.toLocaleString()}</Td>
                        <Td>
                          {new Date(item.ProductionDate).toLocaleDateString(
                            "th-TH",
                            { year: "numeric", month: "long", day: "numeric" }
                          )}
                        </Td>
                        <Td>
                          {new Date(item.ExpiryDate).toLocaleDateString("th-TH", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </Td>
                        <Td>{item.ExpiryDays}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            <Box p={5} textAlign="right">
              <IconButton.Add onClick={handleAddDetail} h={9} />
            </Box>
          </Box>
 */}
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iOrange.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading
                  size={{ base: "md", md: "lg" }}
                  fontWeight="normal"
                >
                  รายการวัคซีนรับเข้าคลัง
                </Heading>
              </HStack>
            </Box>
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </Box>
          </Box>
{/*
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iGreen.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  การจัดส่งวัคซีน
                </Heading>
              </HStack>
            </Box>
            <Box p={5} overflowX="auto">
              <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      แผนการจัดส่งวัคซีน
                    </FormLabel>
                    <Input
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      colorScheme="iGreen"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      ทะเบียนรถขนส่ง
                    </FormLabel>
                    <Input
                      name="deliveryVehicle"
                      value={formData.deliveryVehicle}
                      onChange={handleInputChange}
                      colorScheme="iGreen"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      พนักงานขับรถ
                    </FormLabel>
                    <Input
                      name="deliveryBy"
                      value={formData.deliveryBy}
                      onChange={handleInputChange}
                      colorScheme="iGreen"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      เจ้าหน้าที่ผู้จัดส่ง
                    </FormLabel>
                    <Input
                      name="deliveryStaff"
                      value={formData.deliveryStaff}
                      onChange={handleInputChange}
                      colorScheme="iGreen"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      ข้อมูลเส้นทางการขนส่งวัคซีน
                    </FormLabel>
                    <Input
                      name="deliveryRoute"
                      value={formData.deliveryRoute}
                      onChange={handleInputChange}
                      colorScheme="iGreen"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      อุณหภูมิการขนส่งวัคซีน (แนบไฟล์ JPEG, PNG, PDF, XLSX, XLS)
                    </FormLabel>
                    {!temperatureFile || isEditingFile ? (
                      <Box
                        {...getRootProps()}
                        border="2px dashed"
                        borderColor={isDragActive ? "green.500" : "gray.300"}
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        bg={isDragActive ? "green.50" : "transparent"}
                      >
                        <input {...getInputProps()} />
                        <FontAwesomeIcon icon={faFileUpload} size="2x" color="gray" />
                        <Box mt={2}>
                          {isDragActive
                            ? "วางไฟล์ที่นี่ ..."
                            : "ลากและวางไฟล์ที่นี่ หรือคลิกเพื่อเลือกไฟล์ (JPEG, PNG, PDF, XLSX, XLS)"}
                        </Box>
                      </Box>
                    ) : (
                      <HStack mt={2}>
                        <Box>{temperatureFile.name}</Box>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleEditFile}
                          leftIcon={<FontAwesomeIcon icon={faEdit} />}
                        >
                          แก้ไข
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          onClick={handleRemoveFile}
                          leftIcon={<FontAwesomeIcon icon={faTrash} />}
                        >
                          ลบไฟล์
                        </Button>
                      </HStack>
                    )}
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          </Box>
*/}

          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/vaccinepreventionreceivelist")}
                label="ย้อนกลับ"
              />
{/*
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="blue"
                onClick={() => {
                  console.log("Print Requisition button clicked. FormData:", formData);
                  printTable();
                }}
              >
                พิมพ์ใบเบิก
              </Button>
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="teal"
                onClick={() => {
                  console.log("Print Delivery Note button clicked. FormData:", formData);
                  printDeliveryNote();
                }}
              >
                พิมพ์ใบนำส่ง
              </Button>
*/}
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="purple"
                onClick={() => {
                  console.log("Print Inspection Note button clicked. FormData:", formData);
                  printInspectionNote();
                }}
              >
                พิมพ์ใบตรวจนับ
              </Button>

              {formData.Status === 'Approved' && (
                            <IconButton.Confirm
                              onClick={() => handleConfirm({})} // Explicitly pass an empty object
                              isLoading={isLoading}  label="ยืนยันการรับเข้าคลัง" 
                            />
              )}
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VaccinePreventionRequisitionForm;